import { useState, useRef } from 'react';
import readXlsxFile, { readSheetNames } from 'read-excel-file';
import { CloudArrowUpIcon } from '@heroicons/react/20/solid';
import { toast } from 'react-toastify';

// Reads an .xlsx file in the browser, lets the admin pick the sheet and map
// the columns, and hands the parsed rows to the parent.
//
// fields: [{ key, label, required, match: ['header', ...] }]
//   match holds lower-case header names that select the column automatically.
// onChange(rows | null): rows is a list of objects keyed by field key, or null
//   while the mapping is incomplete.

function headerText(value) {
    return value === null || value === undefined ? '' : String(value).trim();
}

function normalize(value) {
    return headerText(value).toLowerCase();
}

function cellValue(value) {
    if (value instanceof Date) {
        return value.toISOString();
    }
    return value === undefined ? null : value;
}

function detectMapping(fields, columns) {
    const headers = columns.map(normalize);
    const mapping = {};
    fields.forEach((field) => {
        let index = -1;
        for (const candidate of field.match) {
            index = headers.indexOf(candidate);
            if (index >= 0) break;
        }
        if (index < 0) {
            index = headers.findIndex((h) => h && field.match.some((candidate) => h.includes(candidate)));
        }
        mapping[field.key] = index >= 0 ? String(index) : '';
    });
    return mapping;
}

// The header is not always the first row (title rows, blank rows). Look at the
// first rows and take the one that matches the most fields.
const HEADER_SEARCH_ROWS = 25;

function findHeader(fields, rows) {
    let index = 0;
    let score = -1;
    const limit = Math.min(rows ? rows.length : 0, HEADER_SEARCH_ROWS);
    for (let i = 0; i < limit; i++) {
        const mapping = detectMapping(fields, rows[i] || []);
        const matched = fields.filter((f) => mapping[f.key] !== '').length;
        if (matched > score) {
            index = i;
            score = matched;
        }
    }
    return { index: score > 0 ? index : 0, score };
}

function scoreSheet(fields, rows) {
    if (!rows || rows.length < 2) return -1;
    return findHeader(fields, rows).score;
}

export default function ExcelImport({ fields, onChange }) {
    const [file, setFile] = useState(null);
    const [sheets, setSheets] = useState([]);
    const [sheet, setSheet] = useState('');
    const [rows, setRows] = useState([]);
    const [mapping, setMapping] = useState({});
    const [busy, setBusy] = useState(false);
    const input = useRef(null);

    const columns = rows.length > 0 ? rows[0].map(headerText) : [];
    const dataRows = rows.length > 1 ? rows.length - 1 : 0;

    function emit(sheetRows, sheetMapping) {
        const complete = fields.every((f) => !f.required || sheetMapping[f.key] !== '');
        if (!complete || sheetRows.length < 2) {
            onChange(null);
            return;
        }
        const result = [];
        for (let i = 1; i < sheetRows.length; i++) {
            const row = {};
            let hasValue = false;
            fields.forEach((f) => {
                const index = sheetMapping[f.key];
                const value = index === '' ? null : cellValue(sheetRows[i][Number(index)]);
                if (value !== null && value !== '') hasValue = true;
                row[f.key] = value;
            });
            if (hasValue) result.push(row);
        }
        onChange(result);
    }

    function applySheet(selectedFile, name, allRows) {
        const sheetRows = allRows.slice(findHeader(fields, allRows).index);
        const sheetMapping = detectMapping(fields, sheetRows.length > 0 ? sheetRows[0] : []);
        setSheet(name);
        setRows(sheetRows);
        setMapping(sheetMapping);
        emit(sheetRows, sheetMapping);
    }

    async function handleFile(e) {
        const selected = e.target.files && e.target.files[0];
        if (!selected) return;
        setBusy(true);
        try {
            const names = await readSheetNames(selected);
            const contents = await Promise.all(names.map((name) => readXlsxFile(selected, { sheet: name })));
            let best = 0;
            let bestScore = -1;
            contents.forEach((sheetRows, index) => {
                const score = scoreSheet(fields, sheetRows);
                if (score > bestScore) {
                    best = index;
                    bestScore = score;
                }
            });
            setFile(selected);
            setSheets(names);
            applySheet(selected, names[best], contents[best]);
        } catch (err) {
            console.log(err);
            toast.error('Het bestand kon niet gelezen worden. Is het een .xlsx bestand?');
            reset();
        } finally {
            setBusy(false);
        }
    }

    async function changeSheet(name) {
        setBusy(true);
        try {
            const sheetRows = await readXlsxFile(file, { sheet: name });
            applySheet(file, name, sheetRows);
        } catch (err) {
            console.log(err);
            toast.error('Het werkblad kon niet gelezen worden.');
        } finally {
            setBusy(false);
        }
    }

    function changeMapping(key, value) {
        const next = { ...mapping, [key]: value };
        setMapping(next);
        emit(rows, next);
    }

    function reset() {
        setFile(null);
        setSheets([]);
        setSheet('');
        setRows([]);
        setMapping({});
        if (input.current) input.current.value = '';
        onChange(null);
    }

    const selectClass = 'w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';

    if (!file) {
        return (
            <label className="flex justify-center w-full h-32 px-4 transition bg-white border-2 border-gray-300 border-dashed rounded-md appearance-none cursor-pointer hover:border-gray-400 focus:outline-none">
                <span className="flex items-center space-x-2 text-gray-400">
                    <CloudArrowUpIcon className="w-6 h-6" />
                    <span className="font-medium">
                        {busy ? 'Bestand wordt gelezen.' : (
                            <>Sleep je Excel-bestand hierheen of&nbsp;<span className="text-indigo-600">selecteer</span>.</>
                        )}
                    </span>
                </span>
                <input
                    type="file"
                    name="excel"
                    accept=".xlsx"
                    onChange={handleFile}
                    ref={input}
                    className="hidden"
                />
            </label>
        );
    }

    return (
        <div className="text-sm text-gray-700">
            <div className="flex items-center justify-between">
                <p className="truncate">
                    <span className="font-medium text-gray-900">{file.name}</span>
                    <span className="text-gray-500"> ({dataRows} rijen)</span>
                </p>
                <button type="button" className="ml-3 shrink-0 text-indigo-600 hover:text-indigo-800" onClick={reset}>
                    Ander bestand
                </button>
            </div>

            {sheets.length > 1 ? (
                <div className="mt-3 flex items-center">
                    <label className="w-1/2 text-gray-500">Werkblad</label>
                    <select className={selectClass} value={sheet} onChange={(e) => changeSheet(e.target.value)} disabled={busy}>
                        {sheets.map((name) => (
                            <option key={name} value={name}>{name}</option>
                        ))}
                    </select>
                </div>
            ) : null}

            <div className="mt-3 space-y-2">
                {fields.map((field) => (
                    <div key={field.key} className="flex items-center">
                        <label className="w-1/2 text-gray-500">
                            {field.label}{field.required ? '' : <span className="text-gray-400"> (optioneel)</span>}
                        </label>
                        <select
                            className={selectClass}
                            value={mapping[field.key] ?? ''}
                            onChange={(e) => changeMapping(field.key, e.target.value)}
                            disabled={busy}
                        >
                            <option value="">{field.required ? 'Selecteer een kolom' : 'Niet in bestand'}</option>
                            {columns.map((name, index) => (
                                <option key={index} value={String(index)}>{name || `Kolom ${index + 1}`}</option>
                            ))}
                        </select>
                    </div>
                ))}
            </div>
            {dataRows === 0 ? <p className="mt-3 text-gray-500">Dit werkblad bevat geen gegevens onder de header.</p> : null}
        </div>
    );
}

// Renders the server's summary of a (dry run) import.
export function ImportSummary({ result, meeting, showSkipped = true }) {
    if (!result) return null;

    const lines = [
        ['Rijen in bestand', result.rows],
        ['Unieke e-mailadressen', result.unique_emails],
        ['Nieuwe gebruikers', result.users_new],
        ['Bestaande gebruikers', result.users_existing],
    ];
    if (meeting) {
        lines.push(['Nieuwe uitnodigingen', result.invites_new]);
        lines.push(['Aandelen aangepast', result.invites_updated]);
        lines.push(['Al uitgenodigd, ongewijzigd', result.invites_unchanged]);
    }
    lines.push(['Rijen zonder e-mailadres', result.without_email.length]);
    lines.push(['Ongeldige e-mailadressen', result.invalid_email.length]);

    function userStatus(entry) {
        return entry.user_status === 'new' ? 'nieuw' : 'bestaat';
    }

    function inviteStatus(entry) {
        if (entry.invite_status === 'new') return 'nieuw';
        if (entry.invite_status === 'update') return `${entry.current_shares || '/'} wordt ${entry.shares}`;
        return 'ongewijzigd';
    }

    return (
        <div className="text-sm text-gray-700">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                {lines.map(([label, value]) => (
                    <div key={label} className="contents">
                        <dt className="text-gray-500">{label}</dt>
                        <dd className="text-right font-medium text-gray-900">{value}</dd>
                    </div>
                ))}
            </dl>

            {showSkipped && result.without_email.length > 0 ? (
                <p className="mt-3 text-gray-500">
                    <span className="font-medium text-gray-700">Zonder e-mailadres, overgeslagen:</span>{' '}
                    {result.without_email.map((item) => item.name).join(', ')}
                </p>
            ) : null}
            {showSkipped && result.invalid_email.length > 0 ? (
                <p className="mt-3 text-gray-500">
                    <span className="font-medium text-gray-700">Ongeldig e-mailadres, overgeslagen:</span>{' '}
                    {result.invalid_email.map((item) => `${item.name} (${item.email})`).join(', ')}
                </p>
            ) : null}

            {result.entries.length > 0 ? (
                <div className="mt-4 max-h-64 overflow-y-auto rounded-md border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                        <thead className="bg-gray-50 text-left text-gray-500">
                            <tr>
                                <th className="px-2 py-1.5 font-medium">Naam</th>
                                <th className="px-2 py-1.5 font-medium">E-mail</th>
                                {meeting ? <th className="px-2 py-1.5 text-right font-medium">Aandelen</th> : null}
                                <th className="px-2 py-1.5 font-medium">Gebruiker</th>
                                {meeting ? <th className="px-2 py-1.5 font-medium">Uitnodiging</th> : null}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                            {result.entries.map((entry) => (
                                <tr key={entry.email}>
                                    <td className="px-2 py-1.5 text-gray-900">
                                        {entry.name}
                                        {entry.rows > 1 ? <span className="text-gray-400"> ({entry.rows} rijen)</span> : null}
                                    </td>
                                    <td className="px-2 py-1.5 text-gray-500">{entry.email}</td>
                                    {meeting ? <td className="px-2 py-1.5 text-right text-gray-900">{entry.shares ?? '/'}</td> : null}
                                    <td className="px-2 py-1.5 text-gray-500">{userStatus(entry)}</td>
                                    {meeting ? <td className="px-2 py-1.5 text-gray-500">{inviteStatus(entry)}</td> : null}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
        </div>
    );
}

// Text for a toast when an import request fails.
export function importErrorText(err) {
    if (err && err.response && typeof err.response.data === 'string') {
        return err.response.data;
    }
    return 'De import is mislukt. Probeer het opnieuw.';
}

// Names the preview reported without a usable e-mail address. The admin types
// an address per name or skips it; applyEmailFixes writes the typed addresses
// into the rows (by the row indexes the server returned) before the import.
function itemKey(item) {
    return `${item.kind}:${item.name}:${item.email || ''}`;
}

export function buildMissingItems(result) {
    if (!result) return [];
    return result.without_email.map((item) => ({ ...item, kind: 'missing' }))
        .concat(result.invalid_email.map((item) => ({ ...item, kind: 'invalid' })));
}

function fixFor(fixes, item) {
    return fixes[itemKey(item)] || { email: '', skip: false };
}

export function unresolvedMissing(items, fixes) {
    return items.filter((item) => {
        const fix = fixFor(fixes, item);
        return !fix.skip && !fix.email.trim();
    });
}

export function typedEmails(fixes) {
    return Object.values(fixes)
        .filter((fix) => !fix.skip && fix.email.trim())
        .map((fix) => fix.email.trim().toLowerCase());
}

export function applyEmailFixes(rows, items, fixes) {
    const next = rows.slice();
    items.forEach((item) => {
        const fix = fixFor(fixes, item);
        const email = fix.email.trim();
        if (fix.skip || !email) return;
        item.indexes.forEach((index) => {
            if (next[index]) next[index] = { ...next[index], email };
        });
    });
    return next;
}

export function MissingEmails({ items, fixes, onChange, invalidEmails, meeting }) {
    if (!items || items.length === 0) return null;
    const rejected = (invalidEmails || []).map((email) => email.toLowerCase());

    function update(item, patch) {
        const key = itemKey(item);
        onChange({ ...fixes, [key]: { ...fixFor(fixes, item), ...patch } });
    }

    const inputClass = 'w-full rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-400';

    return (
        <div className="mt-4 rounded-md border border-gray-300 p-3 text-sm">
            <p className="text-gray-700">
                <span className="font-medium">Let op:</span>{' '}
                {items.length === 1 ? 'één naam heeft' : `${items.length} namen hebben`} geen bruikbaar e-mailadres.
                Zonder adres kan er geen gebruiker of uitnodiging aangemaakt worden. Vul het adres in, of vink Overslaan aan.
            </p>
            <table className="mt-3 min-w-full text-xs">
                <thead className="text-left text-gray-500">
                    <tr>
                        <th className="py-1 pr-2 font-medium">Naam</th>
                        <th className="py-1 pr-2 text-right font-medium">Rijen</th>
                        {meeting ? <th className="py-1 pr-2 text-right font-medium">Aandelen</th> : null}
                        <th className="py-1 pr-2 font-medium">E-mailadres</th>
                        <th className="py-1 font-medium">Overslaan</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item) => {
                        const fix = fixFor(fixes, item);
                        const typed = fix.email.trim().toLowerCase();
                        const bad = typed && rejected.includes(typed);
                        return (
                            <tr key={itemKey(item)} className="align-top">
                                <td className="py-1 pr-2 text-gray-900">
                                    {item.name}
                                    {item.kind === 'invalid' ? <span className="text-gray-400"> (in bestand: {item.email})</span> : null}
                                </td>
                                <td className="py-1 pr-2 text-right text-gray-500">{item.rows}</td>
                                {meeting ? <td className="py-1 pr-2 text-right text-gray-900">{item.shares ?? '/'}</td> : null}
                                <td className="py-1 pr-2">
                                    <input
                                        type="email"
                                        className={inputClass}
                                        placeholder="naam@voorbeeld.be"
                                        value={fix.email}
                                        disabled={fix.skip}
                                        onChange={(e) => update(item, { email: e.target.value })}
                                    />
                                    {bad ? <p className="mt-1 text-gray-500">Dit adres is ongeldig.</p> : null}
                                </td>
                                <td className="py-1">
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        checked={fix.skip}
                                        onChange={(e) => update(item, { skip: e.target.checked })}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
